// The few building blocks every page uses. No UI kit: Tailwind on the add-in's tokens.
import { useEffect, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

type Variant = 'primary' | 'secondary' | 'danger' | 'ghost';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-white hover:bg-accent-hover disabled:bg-send-idle disabled:text-faint',
  secondary: 'border border-line-btn bg-white text-ink hover:bg-hover disabled:opacity-60',
  danger: 'border border-[#e8c9c5] bg-white text-danger hover:bg-danger-bg disabled:opacity-60',
  ghost: 'text-muted hover:bg-hover-soft hover:text-ink disabled:opacity-60',
};

export function Button({ variant = 'secondary', busy, className = '', children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; busy?: boolean }) {
  return (
    <button type="button" {...rest} disabled={rest.disabled || busy} className={`inline-flex h-8 items-center justify-center gap-1.5 whitespace-nowrap rounded-[5px] px-3 text-[12.5px] font-medium transition-colors disabled:cursor-not-allowed ${VARIANTS[variant]} ${className}`}>
      {busy && <Spinner />}
      {children}
    </button>
  );
}

export function Spinner() {
  return <span aria-hidden className="inline-block h-3 w-3 animate-pc-spin rounded-full border-[1.5px] border-current border-r-transparent" />;
}

const FIELD = 'w-full rounded-md border border-line-btn bg-white px-2.5 py-[6px] text-[13px] text-ink placeholder:text-fainter focus:border-accent focus:outline-hidden focus:ring-0 disabled:bg-code disabled:text-muted';

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} className={`${FIELD} ${props.className ?? ''}`} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea {...props} className={`${FIELD} min-h-[72px] ${props.className ?? ''}`} />;
}

export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return <select {...props} className={`${FIELD} pr-8 ${props.className ?? ''}`} />;
}

export function Checkbox({ label, ...rest }: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className="inline-flex items-center gap-2 text-[13px] text-ink">
      <input type="checkbox" {...rest} className="h-4 w-4 rounded-sm border-line-btn text-accent focus:ring-accent" />
      {label}
    </label>
  );
}

export function Field({ label, hint, children, className = '' }: { label: ReactNode; hint?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <label className={`flex flex-col gap-1 ${className}`}>
      <span className="font-mono text-[11px] uppercase tracking-[0.06em] text-faint">{label}</span>
      {children}
      {hint && <span className="text-[11.5px] text-faint">{hint}</span>}
    </label>
  );
}

export type Tone = 'neutral' | 'good' | 'warn' | 'bad' | 'accent';

const TONES: Record<Tone, string> = {
  neutral: 'bg-hover text-muted',
  good: 'bg-accent-bg text-accent-dark',
  warn: 'bg-warn-bg text-warn',
  bad: 'bg-danger-bg text-danger',
  accent: 'bg-accent text-white',
};

export function Badge({ tone = 'neutral', children }: { tone?: Tone; children: ReactNode }) {
  return <span className={`inline-flex items-center rounded-full px-2 py-px font-mono text-[10.5px] uppercase tracking-[0.04em] ${TONES[tone]}`}>{children}</span>;
}

export function statusTone(status: string): Tone {
  switch (status) {
    case 'active':
    case 'sent':
    case 'paid':
    case 'verified':
      return 'good';
    case 'suspended':
    case 'queued':
    case 'pending':
    case 'draft':
      return 'warn';
    case 'archived':
    case 'failed':
    case 'void':
      return 'bad';
    default:
      return 'neutral';
  }
}

export function Card({ title, actions, children, className = '' }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={`rounded-md border border-line-card bg-white ${className}`}>
      {(title || actions) && (
        <header className="flex items-center justify-between border-b border-line-soft px-4 py-2.5">
          <h2 className="text-[13px] font-medium text-ink">{title}</h2>
          <div className="flex items-center gap-2">{actions}</div>
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Alert({ tone = 'bad', children }: { tone?: Tone; children: ReactNode }) {
  const styles = tone === 'bad' ? 'border-[#e8c9c5] bg-danger-bg text-danger' : tone === 'good' ? 'border-line-soft bg-accent-bg text-accent-dark' : 'border-line-soft bg-warn-bg text-warn';
  return (
    <div role={tone === 'bad' ? 'alert' : 'status'} className={`rounded-md border px-3 py-2 text-[12.5px] ${styles}`}>
      {children}
    </div>
  );
}

/** Table shell: columns are plain headers, rows are rendered by the caller. */
export function Table({ head, children, empty }: { head: ReactNode[]; children: ReactNode; empty?: ReactNode }) {
  const hasRows = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <div className="overflow-x-auto rounded-md border border-line-card bg-white">
      <table className="w-full text-left text-[12.5px]">
        <thead>
          <tr className="border-b border-line-soft bg-code">
            {head.map((h, i) => (
              <th key={i} className="whitespace-nowrap px-3 py-2 font-mono text-[10.5px] font-medium uppercase tracking-[0.06em] text-faint">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {hasRows ? (
            children
          ) : (
            <tr>
              <td colSpan={head.length} className="px-3 py-6 text-center text-faint">
                {empty ?? '—'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Row({ children, onClick, className = '' }: { children: ReactNode; onClick?: () => void; className?: string }) {
  return (
    <tr onClick={onClick} className={`border-b border-line-row last:border-0 ${onClick ? 'cursor-pointer hover:bg-hover-soft' : ''} ${className}`}>
      {children}
    </tr>
  );
}

export function Cell({ children, mono, className = '', title }: { children: ReactNode; mono?: boolean; className?: string; title?: string }) {
  return (
    <td title={title} className={`px-3 py-2 align-middle ${mono ? 'font-mono text-[11.5px]' : ''} ${className}`}>
      {children}
    </td>
  );
}

export function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);
  return (
    <div className="flex items-center justify-between py-2 font-mono text-[11px] text-faint">
      <span data-testid="pager-range">{t('common.range', { from, to, total })}</span>
      <div className="flex items-center gap-1">
        <Button variant="ghost" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label={t('common.previous')}>
          ←
        </Button>
        <span>
          {page} / {pages}
        </span>
        <Button variant="ghost" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label={t('common.next')}>
          →
        </Button>
      </div>
    </div>
  );
}

/** A confirmation dialog; `typed` demands the user type that word (destructive actions). */
export function Confirm({ open, title, message, confirmLabel, typed, danger, busy, onConfirm, onCancel }: {
  open: boolean;
  title: ReactNode;
  message?: ReactNode;
  confirmLabel: ReactNode;
  typed?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  useEffect(() => {
    if (!open) setValue('');
  }, [open]);
  if (!open) return null;
  const ready = !typed || value.trim() === typed;
  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-ink/30 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-md border border-line-card bg-white p-5 shadow-lg animate-pc-in">
        <h2 className="text-[15px] font-medium text-ink">{title}</h2>
        {message && <div className="mt-2 text-[13px] text-muted">{message}</div>}
        {typed && (
          <Field label={t('common.typeToConfirm', { word: typed })} className="mt-3">
            <Input value={value} onChange={(e) => setValue(e.target.value)} autoFocus data-testid="confirm-typed" />
          </Field>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} disabled={!ready} busy={busy} data-testid="confirm-ok">
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function Tabs<T extends string>({ tabs, active, onChange }: { tabs: { id: T; label: ReactNode; count?: number }[]; active: T; onChange: (id: T) => void }) {
  return (
    <div className="flex gap-1 border-b border-line-soft" role="tablist">
      {tabs.map((tab) => (
        <button key={tab.id} type="button" role="tab" aria-selected={tab.id === active} onClick={() => onChange(tab.id)} className={`-mb-px border-b-2 px-3 py-2 text-[12.5px] ${tab.id === active ? 'border-accent font-medium text-ink' : 'border-transparent text-muted hover:text-ink'}`}>
          {tab.label}
          {tab.count !== undefined && <span className="ml-1.5 rounded-full bg-hover px-1.5 font-mono text-[10px] text-faint">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function PageTitle({ title, subtitle, actions }: { title: ReactNode; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-[20px] font-medium leading-tight tracking-[-0.02em] text-ink">{title}</h1>
        {subtitle && <p className="mt-1 text-[12.5px] text-muted">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2">{actions}</div>}
    </div>
  );
}

export function Stat({ label, value, hint, tone }: { label: ReactNode; value: ReactNode; hint?: ReactNode; tone?: Tone }) {
  return (
    <div className="rounded-md border border-line-card bg-white px-4 py-3">
      <div className="font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">{label}</div>
      <div className={`mt-1 text-[22px] font-medium leading-none tracking-[-0.02em] ${tone === 'bad' ? 'text-danger' : tone === 'warn' ? 'text-warn' : 'text-ink'}`}>{value}</div>
      {hint && <div className="mt-1 text-[11.5px] text-faint">{hint}</div>}
    </div>
  );
}

/** Label/value pairs. The label column tracks the widest label but never past `max-w`, so a long label wraps
 *  instead of eating the value column; both tracks can shrink to 0 so the list never widens a narrow card. */
export function KeyValue({ rows }: { rows: [ReactNode, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-[minmax(0,max-content)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[12.5px]">
      {rows.map(([k, v], i) => (
        <div key={i} className="contents">
          <dt className="max-w-44 wrap-break-word font-mono text-[11px] uppercase leading-snug tracking-[0.06em] text-faint">{k}</dt>
          <dd className="min-w-0 wrap-break-word text-ink">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Filters({ children, onReset }: { children: ReactNode; onReset?: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="mb-3 flex flex-wrap items-end gap-2 rounded-md border border-line-card bg-white p-3">
      {children}
      {onReset && (
        <Button variant="ghost" onClick={onReset}>
          {t('common.reset')}
        </Button>
      )}
    </div>
  );
}

export function useList<T>(initial: T) {
  return useState<T>(initial);
}
